import { html } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';

import Localization from 'synapse-js/localization/localization';
import 'synapse-js/ui-components/synapse-spinner';
import { getOsIcon, getDeviceIcon } from './get-os.js';

import proLogo from './pro-icon.js';

let localization = Localization.getInstance();

function calcStatusColor(status) {
  switch (status) {
    case 'Online':
      return '#00ff00';
    case 'In Session':
      return '#ffc800';
    default:
      return '#636363';
  }
}

/*
 * @param {Number} timeRange
 * @returns {String}
 */
function humanize(timeRange) {
  if (timeRange < 60 * 1000) {
    return `${Math.floor(timeRange / 1000)}${localization.get('FIELD-FORMATTER', 'shortSeconds')}`;
  } else if (timeRange < 60 * 60 * 1000) {
    return `${Math.floor(timeRange / (1000 * 60))}${localization.get('FIELD-FORMATTER', 'shortMinutes')}`;
  } else if (timeRange < 24 * 60 * 60 * 1000) {
    return `${Math.floor(timeRange / (1000 * 60 * 60))}${localization.get('FIELD-FORMATTER', 'shortHours')}`;
  }
  return `${Math.floor(timeRange / (1000 * 60 * 60 * 24))}${localization.get('FIELD-FORMATTER', 'shortDays')}`;
}

function stringToMACAddressFormat(data) {
  const formatedMacAddresses = data.map(macAddress => {
    let deviceMac = macAddress.trim();
    deviceMac = deviceMac
      .substring(0, deviceMac.length - 4)
      .toUpperCase()
      .match(/.{1,2}/g)
      .join(':');

    return deviceMac;
  });
  return formatedMacAddresses.join(', ');
}

/**
 *
 * @param { number } timestamp
 * @returns { string }
 */
function formatDate(timestamp) {
  const date = new Date(timestamp);

  return date.toLocaleDateString('default', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour12: true,
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getStatusMessage(row) {
  let message = '';
  if (row?.status === 'Online') {
    message = row?.locked
      ? localization.get('FIELD-FORMATTER', 'nobody')
      : localization.get('FIELD-FORMATTER', 'present');
  } else if (row?.status === 'Offline' && row?.lastSeen) {
    const oneHour = 60 * 60 * 1000;
    const offlineSince = Date.now() - +new Date(row.lastSeen);
    message =
      offlineSince < oneHour
        ? localization.get('FIELD-FORMATTER', 'lessThanAnHour')
        : localization.get('FIELD-FORMATTER', 'duration', humanize(offlineSince));
  }
  return message;
}

function getStatusLocalization(status) {
  switch (status) {
    case 'Online':
      return localization.get('FIELD-FORMATTER', 'online');
    case 'Offline':
      return localization.get('FIELD-FORMATTER', 'offline');
    case 'In Session':
      return localization.get('FIELD-FORMATTER', 'inSession');
    default:
      return '';
  }
}

export default {
  loadLocalization: loc => {
    if (!localization.loaded) {
      localization = loc;
    }
  },
  preferred: isPreferred =>
    html`<div
      style=${styleMap({
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        color: 'var(--icon-color-brand-default)',
        '--goto-icon-size': '20px',
      })}
      title=${isPreferred ? localization.get('FIELD-FORMATTER', 'proDeviceDescription') : ''}
    >
      ${isPreferred ? html`${proLogo}` : html`<div style="width: 24px; height: 24px;"></div>`}
    </div>`,
  deviceName: (name, row) =>
    html` <div
      title=${name}
      style=${styleMap({
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
      })}
    >
      <div>${getDeviceIcon(row.osName)}</div>
      <div
        id="name"
        style=${styleMap({ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '11rem' })}
      >
        ${name}
      </div>
    </div>`,
  group: group => html`<div>${group?.name || localization.get('FIELD-FORMATTER', 'notInGroup')}</div>`,
  status: (status, row) => {
    const message = getStatusMessage(row);
    if (row?.progressing) {
      return html`<synapse-spinner
          style="position: static;--spinnerColor:var(--type-color-default);--spinnerSize:12px;"
        ></synapse-spinner
        >${localization.get('FIELD-FORMATTER', 'progressing')}`;
    }
    return html` <div
      style=${styleMap({
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
      })}
    >
      <div
        style=${styleMap({
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          flex: 'none',
          backgroundColor: calcStatusColor(status),
        })}
      >
        <div
          style=${styleMap({
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: 'var(--surface-color-default)',
            margin: '2px 2px 2px 2px',
            display: row?.locked && status === 'Online' ? 'block' : 'none',
          })}
        ></div>
      </div>
      <div
        style=${styleMap({
          marginLeft: '10px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: 'block',
        })}
      >
        ${getStatusLocalization(status)} <br />${message}
      </div>
    </div>`;
  },
  statusOnCards: (status, row) => {
    if (row?.progressing) {
      return html`<synapse-spinner
          style="margin-left:15px;position: static;--spinnerColor:var(--type-color-default);--spinnerSize:12px;"
        ></synapse-spinner
        >${localization.get('FIELD-FORMATTER', 'progressing')}`;
    }

    const message = getStatusMessage(row);
    return html` <div id="statusOnCards">
      <div
        style=${styleMap({
          width: '0.5rem',
          height: '0.5rem',
          borderRadius: '50%',
          flex: 'none',
          backgroundColor: calcStatusColor(status),
          margin: '10px 15px',
        })}
      >
        <div
          style=${styleMap({
            width: '0.25px',
            height: '0.25px',
            borderRadius: '50%',
            backgroundColor: 'var(--surface-color-default)',
            margin: '2px 2px 2px 2px',
            display: row?.locked && status === 'Online' ? 'block' : 'none',
          })}
        ></div>
      </div>
      <div>${`${getStatusLocalization(status)} ${message}`}</div>
    </div>`;
  },
  getStatusMessage,
  labelsInTable: labels => {
    if (!labels?.length) {
      return html``;
    }

    return html`<div
      style=${styleMap({
        display: 'flex',
        gap: '4px',
        justifyContent: 'flex-end',
      })}
    >
      <span
        style=${styleMap({
          display: 'inline-block',
          borderRadius: '10px',
          backgroundColor: 'var(--purple-300)',
          color: 'var(--neutral-900)',
          padding: '2px 8px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        })}
      >
        ${labels[0]}
      </span>
      ${labels.length > 1
        ? html`<span
            style=${styleMap({
              borderRadius: '10px',
              backgroundColor: 'var(--purple-300)',
              color: 'var(--neutral-900)',
              padding: '2px 8px',
              marginRight: labels.length > 1 ? '4px' : '',
            })}
          >
            +${labels.length - 1}
          </span>`
        : null}
    </div>`;
  },

  labelsInDrawer: labels =>
    html`${labels && Object.values(labels).length === 0
      ? html`<span
          id="labelsInDrawer"
          style=${styleMap({
            borderRadius: '10px',
            backgroundColor: 'var(--surface-color-secondary)',
            color: 'var(--type-color-disabled)',
            padding: '0.25rem 0.5rem',
            margin: '2px',
          })}
        >
          No Labels
        </span>`
      : html`<div
          style=${styleMap({
            display: 'flex',
            flexWrap: 'wrap',
          })}
        >
          ${Object.values(labels || []).map(
            label =>
              html`<div
                id="labelsInDrawer"
                style=${styleMap({
                  borderRadius: '10px',
                  backgroundColor: 'var(--purple-300)',
                  color: 'var(--neutral-900)',
                  padding: '0.25rem 0.5rem',
                  margin: '2px',
                })}
              >
                ${label}
              </div>`,
          )}
        </div>`}`,

  osName: value =>
    value
      ? html`
          <div
            style=${styleMap({
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'center',
            })}
          >
            <synapse-icon
              style=${styleMap({ flexShrink: '0', padding: '4px' })}
              class="icon"
              .svg="${getOsIcon(value)}"
            ></synapse-icon>
            <div
              style=${styleMap({
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              })}
            >
              ${value}
            </div>
          </div>
        `
      : html``,
  ipAddresses: value => (value ? html` <span> ${value.join(', ')} </span> ` : html``),
  macAddresses: value => (value ? html` <span> ${stringToMACAddressFormat(value)}</span> ` : ''),
  lastAccess: value =>
    value ? html` <span> ${formatDate(value)}</span> ` : html`${localization.get('FIELD-FORMATTER', 'never')}`,
  localDate: value => html`${value ? new Date(value).toLocaleDateString() : ''}`,
  dateWithInterval: timestamp =>
    timestamp
      ? html`
          <div
            style=${styleMap({
              display: 'flex',
              flexDirection: 'column',
            })}
          >
            <span> ${localization.get('FIELD-FORMATTER', 'since', humanize(new Date().getTime() - timestamp))}</span>
            <span> ${formatDate(timestamp)}</span>
          </div>
        `
      : null,
};
